/**
 * Overpass API (OpenStreetMap) - récupération de POIs touristiques / historiques
 * Max 5 POIs autour d'un point. Plusieurs endpoints en secours en cas de 504.
 */

const OVERPASS_ENDPOINTS = [
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass-api.de/api/interpreter'
];

const RADIUS_M = 80000;
const MAX_POIS = 5;
const REQUEST_TIMEOUT_MS = 15000;

export interface Poi {
    id: string;
    name: string;
    lat: number;
    lon: number;
}

interface OverpassElement {
    type: string;
    id: number;
    lat?: number;
    lon?: number;
    center?: { lat: number; lon: number };
    tags?: { name?: string; [k: string]: string | undefined };
}

interface OverpassResponse {
    elements: OverpassElement[];
}

function buildQuery(lat: number, lon: number): string {
    return `[out:json][timeout:8];
(
  node(around:${RADIUS_M},${lat},${lon})["tourism"];
  node(around:${RADIUS_M},${lat},${lon})["historic"];
);
out ${MAX_POIS};
`;
}

function elementToPoi(el: OverpassElement): Poi | null {
    const lat = el.lat ?? el.center?.lat;
    const lon = el.lon ?? el.center?.lon;
    const name = el.tags?.name?.trim();
    if (lat == null || lon == null || !name) return null;
    return {
        id: `${el.type}-${el.id}`,
        name,
        lat,
        lon
    };
}

async function tryFetch(
    url: string,
    query: string,
    signal: AbortSignal
): Promise<Response> {
    return fetch(url, {
        method: 'POST',
        signal,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
        },
        body: `data=${encodeURIComponent(query)}`
    });
}

export async function fetchPois(center: [number, number]): Promise<Poi[]> {
    const [lat, lon] = center;
    const query = buildQuery(lat, lon);

    for (const url of OVERPASS_ENDPOINTS) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const res = await tryFetch(url, query, controller.signal);
            clearTimeout(timeoutId);
            if (!res.ok) continue;
            const data: OverpassResponse = await res.json();
            const pois: Poi[] = [];
            const seen = new Set<string>();
            for (const el of data.elements ?? []) {
                if (pois.length >= MAX_POIS) break;
                const poi = elementToPoi(el);
                if (poi && !seen.has(poi.name)) {
                    seen.add(poi.name);
                    pois.push(poi);
                }
            }
            return pois.slice(0, MAX_POIS);
        } catch {
            clearTimeout(timeoutId);
            continue;
        }
    }
    return [];
}
